import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import sessionStorage from "./utils/sessionStore";
import {
  Container, Row, Col, Card, Form, Button, Alert, Spinner, Badge, Tabs, Tab,
  Table, Modal, ButtonGroup, Dropdown,
} from "react-bootstrap";
import { FaSync, FaCheckCircle, FaExclamationTriangle, FaTrash, FaChevronLeft, FaDownload, FaPlus, FaEnvelope, FaFileInvoiceDollar, FaFileAlt } from "react-icons/fa";
import {
  authHeaders,
  SummaryItem,
} from "./QuoteCreate";
import SearchableSelect from "./components/SearchableSelect";
import { systemsDescriptor } from "./utils/refDataCache";
import { hasAction } from "./utils/auth";
import {
  REPORT_STYLES, DEFAULT_REPORT_STYLE,
  downloadQuoteReport, resendQuoteEmail, sendQuoteReportTo,
} from "./utils/quoteReports";
import ConvertToOrderModal from "./ConvertToOrderModal";
import AttachmentsButton from "./components/ReferenceDocsPanel";
import "./pagecss/Webappcss.css";
import "./pagecss/QuoteWizard.css";

const API_URL = process.env.REACT_APP_API_URL;

// Part-number searchable dropdown — same config the create flow uses so the
// edit page's "add line" picker behaves identically (preloaded system list,
// part # + description).
const AUTOCOMPLETE_PART = {
  apiUrl: "/api/Part/systems/search",
  queryParam: "q",
  dataPath: "systems",
  displayField: "Part_PartNum",
  valueField: "Part_PartNum",
  secondaryField: "Part_PartDescription",
  minChars: 1,
  defaultParams: "top=10",
  preload: true,
  descriptor: systemsDescriptor(),
};

// -----------------------------------------------------------------------------
// Money / pct helpers (mirror QuoteCreate; kept local so this file is self-
// sufficient if QuoteCreate ever changes its formatters).
// -----------------------------------------------------------------------------
function fmtMoney(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(v));
}
function fmtPct(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  return `${Number(v).toFixed(2)}%`;
}
function fmtDateTime(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
}

// =============================================================================
// QuoteEdit — load an existing Epicor quote, edit header/lines/misc, submit
// /api/Sales/quote/update. Three tabs: Edit, Audit Log, and Failed Jobs.
//
// Spec: SalesQuote_UI_Developer_Guide.docx Section 12 (Update Quote Payload).
// =============================================================================
function QuoteEdit() {
  const navigate = useNavigate();
  const { quoteNum: routeQuoteNum } = useParams();

  // Auth gate — same pattern as QuoteCreate.
  useEffect(() => {
    if (!sessionStorage.getItem("authToken")) navigate("/login");
  }, [navigate]);

  // ── Lookup state ─────────────────────────────────────────────────────────
  const [quoteNumInput, setQuoteNumInput] = useState(routeQuoteNum || "");
  const [loadedQuote, setLoadedQuote]     = useState(null);   // QuoteDTO from /api/Quote/{n}
  const [loading, setLoading]             = useState(false);
  const [loadError, setLoadError]         = useState(null);

  // ── Edit state ───────────────────────────────────────────────────────────
  // Header changes (only fields the rep has touched are sent).
  const [headerEdits, setHeaderEdits] = useState({});
  const [lineEdits,   setLineEdits]   = useState({});         // { quoteLine: { quantity?, partNum?, repDiscountPercent? } }
  const [addLines,    setAddLines]    = useState([]);         // [{ partNum, quantity, repDiscountPercent }]
  const [removedLines, setRemovedLines] = useState(new Set()); // Set<int quoteLine>
  const [recalculateTariff, setRecalculateTariff] = useState(null);  // null | true | false
  // Tariff buttons show only while the tariff surcharge is switched on (Kapil 2026-10-31,
  // QUOTE-62). Unknown (call failed) keeps them, as before.
  const [tariffEnabled, setTariffEnabled] = useState(true);
  useEffect(() => {
    fetch(`${API_URL}/api/Sales/tariff-settings`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (j && j.enabled === false) setTariffEnabled(false); })
      .catch(() => {});
  }, []);

  // ── Submit state ─────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  // ── View ↔ Edit mode ───────────────────────────────────────────────────────
  // Default is the read-only review view (mirrors the create-quote review). The
  // "Edit quote" button flips to inline-editable cards. editSessionKey remounts
  // the edit form so its uncontrolled defaultValues reset on enter/cancel.
  const [isEditing, setIsEditing] = useState(false);
  const [editSessionKey, setEditSessionKey] = useState(0);

  // ── Report download / email ──────────────────────────────────────────────
  // Holds the report style currently downloading, not a bare boolean — the
  // Documents modal lists all three styles as rows, so a shared flag made every
  // row say "Preparing…" when one was clicked.
  const [downloading, setDownloading] = useState(null);
  const [reportMsg, setReportMsg]     = useState(null);   // { type: 'success'|'danger', text }
  const [sendOpen, setSendOpen]       = useState(false);

  // ── Convert to sales order ────────────────────────────────────────────────
  const [convertOpen, setConvertOpen] = useState(false);

  const handleDownload = async (variant) => {
    if (!loadedQuote) return;
    setDownloading(variant); setReportMsg(null);
    try {
      await downloadQuoteReport(loadedQuote.quoteNum, variant);
    } catch (e) {
      setReportMsg({ type: "danger", text: `Download failed: ${e.message}` });
    } finally {
      setDownloading(null);
    }
  };

  // ── PO documents ──────────────────────────────────────────────────────────
  // The customer PO(s) attached to this quote (attachmentType "PODocument").
  // Once a quote is closed/converted the rep has no other way to retrieve them,
  // so the toolbar exposes a "Download PO" control. poDocs is null until first
  // loaded, then the non-deleted PODocument rows.
  // Documents drawer — one home for everything file-shaped on this quote:
  // the three quote report styles, the customer POs, and the attachments. They
  // were three separate header controls, two of which were dropdowns and so
  // could not collapse into the overflow menu without nesting a menu in a menu.
  const [docsOpen, setDocsOpen]       = useState(false);
  // Three tabs because these are three different things, not one list: a quote
  // PDF we generate and send OUT, POs the customer sends IN, and internal files
  // that never leave. Different direction, different owner, different action.
  const [docsTab, setDocsTab]         = useState("pdf");
  // Same derivation QuoteHeaderSummary uses (lines + header misc + header tax).
  // The PO tab states the figure a purchase order has to match, so it has to be
  // the amount-due total, not the line total.
  const amountDueForDocs = loadedQuote
    ? Math.round(((Number(loadedQuote.quoteAmt) || 0)
        + (Number(loadedQuote.totalMiscAmt) || 0)
        + (Number(loadedQuote.totalTax) || 0)) * 100) / 100
    : 0;
  const [poDocs, setPoDocs]           = useState(null);
  const [poLoading, setPoLoading]     = useState(false);
  const [poDownloading, setPoDownloading] = useState(false);

  const loadPODocs = useCallback(async () => {
    if (!loadedQuote) return [];
    setPoLoading(true);
    try {
      const r = await fetch(
        `${API_URL}/api/Attachment?quoteNum=${encodeURIComponent(loadedQuote.quoteNum)}&attachmentType=PODocument`,
        { headers: authHeaders() }
      );
      const list = await r.json().catch(() => []);
      const rows = (Array.isArray(list) ? list : []).filter((a) => a.status !== "Deleted");
      setPoDocs(rows);
      return rows;
    } catch (e) {
      setReportMsg({ type: "danger", text: `Couldn't load PO documents: ${e.message}` });
      setPoDocs([]);
      return [];
    } finally {
      setPoLoading(false);
    }
  }, [loadedQuote]);

  // Resolve a single attachment's short-lived download URL and trigger a save.
  const downloadOnePO = async (att) => {
    const r = await fetch(`${API_URL}/api/Attachment/${att.id}/download-url`, { headers: authHeaders() });
    const d = await r.json().catch(() => null);
    const url = d?.downloadUrl || d?.url || d?.sasUrl;
    if (!url) throw new Error(`No download link for ${att.fileName || `PO ${att.id}`}.`);
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    a.download = att.fileName || `PO_${att.id}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // Download every PO attached to the quote as ONE .zip. The browser blocks
  // firing N separate cross-origin saves (and ignores the download attribute on
  // blob URLs), so the server packs them into a single archive we save here —
  // identical behaviour on desktop and mobile.
  const handleDownloadAllPOs = async () => {
    if (!loadedQuote) return;
    setPoDownloading(true); setReportMsg(null);
    try {
      const r = await fetch(
        `${API_URL}/api/Attachment/quote/${encodeURIComponent(loadedQuote.quoteNum)}/po-zip`,
        { headers: authHeaders() }
      );
      if (!r.ok) {
        let msg = `HTTP ${r.status}`;
        try { const d = await r.json(); msg = d.error || d.message || msg; } catch { /* non-JSON body */ }
        if (r.status === 404) { setReportMsg({ type: "info", text: msg }); return; }
        throw new Error(msg);
      }
      const blob = await r.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `Quote_${loadedQuote.quoteNum}_POs.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (e) {
      setReportMsg({ type: "danger", text: `PO download failed: ${e.message}` });
    } finally {
      setPoDownloading(false);
    }
  };

  const poLabel = (att) => {
    const po = att.confirmedPONumber || att.extractedPONumber;
    return po ? `${att.fileName || `PO ${att.id}`} · PO ${po}` : (att.fileName || `PO ${att.id}`);
  };

  // A quote that has been converted to a (valid, non-voided) sales order is
  // read-only — Epicor flips QuoteHed.Ordered back to false when the order is
  // voided, so this flag is the live "an order exists" signal. To edit again,
  // the sales order must be voided first.
  const isConvertedToOrder = !!loadedQuote?.ordered;

  // Multi-shipment quotes never use the one-shot convert modal — they convert
  // per shipment, one PO at a time, on the shipment board. A partially
  // converted MSP quote also flips Epicor's ordered/closed flags without being
  // finished, so none of the one-shot gating below applies to it.
  const isMspQuote = isMspTermsCode(loadedQuote?.termsCode);

  // A quote can be converted only while it's a live, open quote: not already
  // ordered, not closed, not expired, and it must have at least one line.
  // (The backend enforces the same rules; this just gates the button so the rep
  // doesn't open the modal on a quote that can never convert.)
  const canConvert =
    !!loadedQuote &&
    !isConvertedToOrder &&
    !loadedQuote.quoteClosed &&
    !loadedQuote.expired &&
    (loadedQuote.quoteDtls || []).length > 0;

  const convertDisabledReason = !loadedQuote
    ? undefined
    : isConvertedToOrder ? "Already converted to a sales order"
    : loadedQuote.quoteClosed ? "Quote is closed — reopen it before converting"
    : loadedQuote.expired ? "Quote is expired — update its expiration date first"
    : (loadedQuote.quoteDtls || []).length === 0 ? "Quote has no lines"
    : undefined;

  // ── Audit log + jobs state ───────────────────────────────────────────────
  const [logs, setLogs]             = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError]     = useState(null);

  // Job-inspector modal state — { jobId, job?, loading, error }.
  const [jobModal, setJobModal] = useState(null);

  // ───────────────────────────────────────────────────────────────────────────
  // Loaders
  // ───────────────────────────────────────────────────────────────────────────
  const loadQuote = useCallback(async (qn, { keepResult = false } = {}) => {
    if (!qn) return;
    setLoading(true);
    setLoadError(null);
    setLoadedQuote(null);
    setHeaderEdits({});
    setLineEdits({});
    setAddLines([]);
    setRemovedLines(new Set());
    setRecalculateTariff(null);
    // The reload right after a save keeps its "Quote updated successfully." —
    // clearing it here wiped the message before the rep could see it (UI-QED-031).
    if (!keepResult) setSubmitResult(null);
    setIsEditing(false);

    try {
      const r = await fetch(`${API_URL}/api/Quote/${encodeURIComponent(qn)}`, { headers: authHeaders() });
      const data = await r.json().catch(() => ({}));
      if (r.status === 404) {
        setLoadError(`Quote ${qn} not found.`);
      } else if (!r.ok || data.success === false) {
        setLoadError(data.error || data.message || `HTTP ${r.status}`);
      } else {
        setLoadedQuote(data.quote || data);
      }
    } catch (e) {
      setLoadError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLogs = useCallback(async (qn) => {
    if (!qn) return;
    setLogsLoading(true);
    setLogsError(null);
    try {
      const r = await fetch(`${API_URL}/api/Sales/quote-log/${encodeURIComponent(qn)}`, { headers: authHeaders() });
      if (r.status === 404) {
        setLogs([]);
      } else if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        setLogsError(data.message || data.error || `HTTP ${r.status}`);
      } else {
        const data = await r.json();
        setLogs(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      setLogsError(e.message);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  // Close / reopen the quote in Epicor (SAL-GAP-88, Kapil 2026-10-23). Offered only on a
  // quote that was never ordered: a converted quote is already closed by Epicor, and
  // reopening it would let it be converted a second time (one-quote-one-order rule).
  // Closing marks the quote LOST in Epicor with a reason the rep picks from Epicor's own
  // Lost reasons (Kapil 2026-10-23); the API walks Epicor's quote task to do it.
  const [openCloseBusy, setOpenCloseBusy] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [lostReasons, setLostReasons] = useState(null);   // null = loading, [] = none / failed
  const [lostReason, setLostReason] = useState("");
  const startClose = async () => {
    setLostReason("");
    setCloseOpen(true);
    if (lostReasons && lostReasons.length) return;
    setLostReasons(null);
    try {
      const r = await fetch(`${API_URL}/api/Quote/lost-reasons`, { headers: authHeaders() });
      const data = await r.json().catch(() => ({}));
      setLostReasons(r.ok && Array.isArray(data.reasons) ? data.reasons : []);
    } catch {
      setLostReasons([]);
    }
  };
  const openCloseQuote = async (close) => {
    const qn = loadedQuote?.quoteNum;
    if (!qn) return;
    if (!close && !window.confirm(`Reopen quote #${qn}? It can be edited and converted again.`)) return;
    setOpenCloseBusy(true);
    setReportMsg(null);
    try {
      const r = await fetch(`${API_URL}/api/Quote/${encodeURIComponent(qn)}/${close ? "close" : "open"}`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: close ? JSON.stringify({ reasonCode: lostReason }) : undefined,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.success === false) {
        throw new Error(data.error || data.message || `HTTP ${r.status}`);
      }
      setCloseOpen(false);
      await loadQuote(qn);
      loadLogs(qn);
      const why = close ? (lostReasons || []).find((x) => x.reasonCode === lostReason)?.description : null;
      setReportMsg({ type: "success", text: close ? `Quote #${qn} closed as lost${why ? ` (${why})` : ""}.` : `Quote #${qn} reopened.` });
    } catch (e) {
      setReportMsg({ type: "danger", text: `${close ? "Close" : "Reopen"} failed: ${e.message}` });
    } finally {
      setOpenCloseBusy(false);
    }
  };

  // Auto-load if the route includes a quote number (e.g. /quote/edit/12345
  // linked from the existing Quotes search page).
  useEffect(() => {
    if (routeQuoteNum) {
      setQuoteNumInput(String(routeQuoteNum));
      loadQuote(routeQuoteNum);
      loadLogs(routeQuoteNum);
    }
  }, [routeQuoteNum, loadQuote, loadLogs]);

  // ───────────────────────────────────────────────────────────────────────────
  // Job inspector + retry — uses /api/Sales/job/{id} and /quote/retry/{id}
  // ───────────────────────────────────────────────────────────────────────────
  const openJobModal = useCallback(async (jobId) => {
    setJobModal({ jobId, loading: true, job: null, error: null });
    try {
      const r = await fetch(`${API_URL}/api/Sales/job/${encodeURIComponent(jobId)}`, { headers: authHeaders() });
      if (r.status === 404) {
        setJobModal((m) => ({ ...m, loading: false, error: "Job not found." }));
      } else if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        setJobModal((m) => ({ ...m, loading: false, error: data.message || data.error || `HTTP ${r.status}` }));
      } else {
        const data = await r.json();
        setJobModal((m) => ({ ...m, loading: false, job: data, error: null }));
      }
    } catch (e) {
      setJobModal((m) => ({ ...m, loading: false, error: e.message }));
    }
  }, []);

  const retryJob = useCallback(async (jobId) => {
    setJobModal((m) => ({ ...m, retrying: true, retryResult: null }));
    try {
      const r = await fetch(`${API_URL}/api/Sales/quote/retry/${encodeURIComponent(jobId)}`, {
        method: "POST", headers: authHeaders(),
      });
      const data = await r.json().catch(() => ({}));
      setJobModal((m) => ({
        ...m, retrying: false,
        retryResult: { ok: r.ok && data.success !== false, message: data.error || data.message || (r.ok ? "Retry queued." : `HTTP ${r.status}`) },
      }));
      // Refresh logs if retry succeeded — a successful retry creates a new ErpJob row.
      if (r.ok && data.success !== false && loadedQuote?.quoteNum) {
        loadLogs(loadedQuote.quoteNum);
      }
    } catch (e) {
      setJobModal((m) => ({ ...m, retrying: false, retryResult: { ok: false, message: e.message } }));
    }
  }, [loadedQuote, loadLogs]);

  // ───────────────────────────────────────────────────────────────────────────
  // Submit — build UpdateSalesQuoteRequest from local edit state
  // ───────────────────────────────────────────────────────────────────────────
  // New lines are only "real" once they have a part number — empty draft rows
  // don't enable Save or get submitted.
  const validAddLines = addLines.filter((l) => l.partNum && l.partNum.trim() !== "");
  // Gap 16 (2026-10-31): a new line's quantity below 1 used to be sent as 1 without a
  // word. It is now refused on screen with the server rule's own words, and Save waits.
  const badNewLineQty = validAddLines.some((l) => newLineQtyError(l.quantity) !== null);

  const hasAnyChanges = (
    Object.keys(headerEdits).length > 0 ||
    Object.keys(lineEdits).length > 0 ||
    validAddLines.length > 0 ||
    removedLines.size > 0 ||
    recalculateTariff !== null
  );

  const handleSubmit = async () => {
    if (!loadedQuote || !hasAnyChanges) return;
    if (badNewLineQty) {
      const reason = validAddLines.map((l) => newLineQtyError(l.quantity)).find(Boolean) || NEW_LINE_QTY_RULE;
      setSubmitResult({ success: false, error: `${reason} Nothing was saved.` });
      return;
    }
    setSubmitting(true);
    setSubmitResult(null);

    const updateLines = Object.entries(lineEdits)
      .filter(([qLine]) => !removedLines.has(Number(qLine)))
      .map(([qLine, patch]) => ({ quoteLine: Number(qLine), ...patch }));

    // UI-QR-N019 (2026-11-01): 1.5 used to reach the server, which can only read whole
    // numbers, and the rep saw a bare "HTTP 400". Say it here, in plain words, and stop.
    const fractional = updateLines.find((l) => l.quantity != null && !Number.isInteger(Number(l.quantity)));
    if (fractional) {
      setSubmitting(false);
      setSubmitResult({ success: false, error: `Quantity on line ${fractional.quoteLine} must be a whole number (got ${fractional.quantity}). Nothing was saved.` });
      return;
    }

    const newLines = validAddLines.map((l) => ({
      partNum: l.partNum.trim(),
      quantity: Number(l.quantity),   // checked >= 1 above (gap 16) - never silently 1
      repDiscountPercent: Number(l.repDiscountPercent) || 0,
    }));

    const body = {
      quoteNum: loadedQuote.quoteNum,
      ...headerEdits,
      ...(updateLines.length ? { updateLines } : {}),
      ...(newLines.length ? { addLines: newLines } : {}),
      ...(removedLines.size ? { removeLineNumbers: [...removedLines] } : {}),
      ...(recalculateTariff !== null ? { recalculateTariff } : {}),
      updatedBy: sessionStorage.getItem("userEmail") || sessionStorage.getItem("userName") || "UI",
    };

    try {
      const r = await fetch(`${API_URL}/api/Sales/quote/update`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.success === false) {
        setSubmitResult({ success: false, error: data.error || data.message || validationText(data) || `HTTP ${r.status}` });
      } else {
        setSubmitResult({ success: true, ...data });
        setIsEditing(false);
        // Reload quote + logs so the page reflects the post-update state.
        loadQuote(loadedQuote.quoteNum, { keepResult: true });
        loadLogs(loadedQuote.quoteNum);
      }
    } catch (e) {
      setSubmitResult({ success: false, error: e.message });
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = () => {
    if (isConvertedToOrder) return;   // converted quotes are read-only
    setSubmitResult(null);
    setEditSessionKey((k) => k + 1);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setHeaderEdits({});
    setLineEdits({});
    setAddLines([]);
    setRemovedLines(new Set());
    setRecalculateTariff(null);
    setSubmitResult(null);
    setEditSessionKey((k) => k + 1);
    setIsEditing(false);
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Render
  // ───────────────────────────────────────────────────────────────────────────
  // Deliberately NOT .dyn-form-shell. That class caps the container at the
  // viewport height and scrolls the body INSIDE it (NavbarLayout.js:144-152),
  // which suits a short add/edit form but not this page: a loaded quote is a long
  // read-only document (header + pricing summary + every line), and the inner
  // scrollbox meant the browser's own scrollbar did nothing and only ~810px of
  // ~2190px was ever reachable. Without the class this falls into NavbarLayout's
  // "content page" branch — natural height, the page scrolls.
  // .dyn-form-2x stays: it is read at NavbarLayout.js:124 for canvas zoom.
  return (
    <Container fluid className="qw-root content-halo dyn-form-2x" style={{ padding: "18px 20px 26px", borderRadius: 16 }}>
      <div className="dyn-form-scroll">
      {/* ── Page header ────────────────────────────────────────────────────
          Three jobs, three places — the old single band mixed them and ended up
          with nine equally-weighted controls that wrapped onto three rows:

            1. GO SOMEWHERE ELSE — the quote lookup. Navigation, so it sits in
               the breadcrumb strip, not in a toolbar about THIS quote.
            2. WHAT THIS IS       — number, status, customer, amount due.
            3. WHAT YOU CAN DO    — one primary (Convert), one secondary (Edit),
               Documents, and everything rare behind the overflow menu.

          Convert is the only filled button on the page: it is irreversible and
          creates a real sales order, and it previously looked identical to
          Download PO. ── */}
      <div className="qe-crumb">
        <div className="qe-crumb-l">
          <button type="button" onClick={() => navigate("/quote")}>Quotes</button>
          <span className="sep">›</span>
          <span className="cur qw-mono">
            {loadedQuote ? `#${loadedQuote.quoteNum}` : "New lookup"}
          </span>
        </div>
        <div className="qe-lookup">
          <Form.Control
            value={quoteNumInput}
            onChange={(e) => setQuoteNumInput(e.target.value.replace(/\D/g, ""))}
            onKeyDown={(e) => { if (e.key === "Enter" && quoteNumInput) { loadQuote(quoteNumInput); loadLogs(quoteNumInput); } }}
            placeholder="Go to quote #…"
            inputMode="numeric"
            aria-label="Go to quote number"
            size="sm"
            className="qw-mono"
          />
          <Button
            size="sm"
            disabled={!quoteNumInput || loading}
            onClick={() => { loadQuote(quoteNumInput); loadLogs(quoteNumInput); }}
          >
            {loading ? <Spinner animation="border" size="sm" /> : "Go"}
          </Button>
        </div>
      </div>

      <div className="qw-header" style={{ marginBottom: 14, display: "block" }}>
        <div className="qe-ribbon-id">
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <Button
              onClick={() => navigate("/quote")}
              title="Back to quotes"
              aria-label="Back to quotes"
              style={{
                background: "rgba(255,255,255,.18)", color: "#fff", border: "none",
                borderRadius: "50%", width: 36, height: 36, minWidth: 36, padding: 0,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <FaChevronLeft />
            </Button>
            <div className="qw-header-icon"><FaFileInvoiceDollar size={18} /></div>
            <div style={{ minWidth: 0 }}>
              <div className="qw-header-title">
                {loadedQuote ? <>Quote #{loadedQuote.quoteNum}</> : "Edit Sales Quote"}
              </div>
              <div className="qw-header-sub">
                {loadedQuote
                  ? [loadedQuote.customerName || loadedQuote.customerCustID, loadedQuote.salesRepName]
                      .filter(Boolean).join(" · ") || "Sales quote"
                  : "Look up a quote to view or edit"}
              </div>
            </div>
          </div>
          {/* No amount here on purpose. The identity card directly below already
              leads with AMOUNT DUE; repeating it in the band would put the same
              figure on screen twice, 80px apart. The band is identity + actions. */}
        </div>

        {/* View-mode actions. Hidden while editing — the edit toolbar below owns
            Cancel/Save. Same handlers and entitlements as before; only the
            grouping and the visual weight changed. */}
        {loadedQuote && !isEditing && (
        <div className="qe-actions">
          {isMspQuote ? (
            <Button
              className="qw-btn qw-btn-sm qe-primary"
              onClick={() => navigate(`/quote/msp/${loadedQuote.quoteNum}`)}
              title="Multi-shipment quote — POs and conversion happen per shipment on the shipment board"
            >
              <FaFileInvoiceDollar style={{ marginRight: 6 }} /> Shipments<span className="d-none d-sm-inline"> &amp; POs</span>
            </Button>
          ) : hasAction("00039", true) && (
            <Button
              className="qw-btn qw-btn-sm qe-primary"
              onClick={() => { setReportMsg(null); setConvertOpen(true); }}
              disabled={!canConvert}
              title={convertDisabledReason}
            >
              <FaFileInvoiceDollar style={{ marginRight: 6 }} /> Convert<span className="d-none d-sm-inline"> to Sales Order</span>
            </Button>
          )}
          {hasAction("00037", true) && (
            <Button
              className="qw-btn qw-btn-sm qe-secondary"
              onClick={startEdit}
              disabled={isConvertedToOrder}
              title={isConvertedToOrder ? "Converted to a sales order — void the order to edit" : undefined}
            >
              Edit quote
            </Button>
          )}
          <Button
            className="qw-btn qw-btn-sm qe-ghost"
            onClick={() => { if (poDocs === null) loadPODocs(); setDocsOpen(true); }}
            title="Quote PDFs, customer POs and attachments"
          >
            <FaFileAlt style={{ marginRight: 6 }} /> Documents
          </Button>
          <Dropdown align="end">
            <Dropdown.Toggle className="qw-btn qw-btn-sm qe-ghost qe-more" title="More actions" />
            <Dropdown.Menu>
              {hasAction("00035", true) && (
                <Dropdown.Item onClick={() => { setReportMsg(null); setSendOpen(true); }}>
                  <FaEnvelope style={{ marginRight: 8 }} /> Send email
                </Dropdown.Item>
              )}
              {hasAction("00037", true) && !isConvertedToOrder && (
                loadedQuote.quoteClosed ? (
                  <Dropdown.Item disabled={openCloseBusy} onClick={() => openCloseQuote(false)}>
                    <FaCheckCircle style={{ marginRight: 8 }} /> Reopen quote
                  </Dropdown.Item>
                ) : (
                  <Dropdown.Item disabled={openCloseBusy} onClick={startClose}>
                    <FaExclamationTriangle style={{ marginRight: 8 }} /> Close quote
                  </Dropdown.Item>
                )
              )}
              <Dropdown.Item onClick={() => { loadQuote(loadedQuote.quoteNum); loadLogs(loadedQuote.quoteNum); }}>
                <FaSync style={{ marginRight: 8 }} /> Refresh
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
        )}
      </div>

      {/* ── Close as lost: the rep must name Epicor's Lost reason ───────────── */}
      <Modal show={closeOpen} onHide={() => !openCloseBusy && setCloseOpen(false)} centered>
        <Modal.Header closeButton={!openCloseBusy}>
          <Modal.Title style={{ fontSize: 17 }}>Close quote #{loadedQuote?.quoteNum} as lost</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ fontSize: 13, color: "#475569", marginBottom: 10 }}>
            The customer will not buy this quote. It is marked <strong>Lost</strong> in Epicor with the
            reason you pick, and closed. It can be reopened later.
          </div>
          <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>Lost reason</Form.Label>
          {lostReasons === null ? (
            <div style={{ fontSize: 13 }}><Spinner animation="border" size="sm" /> Loading reasons from Epicor…</div>
          ) : lostReasons.length === 0 ? (
            <Alert variant="danger" style={{ fontSize: 13, marginBottom: 0 }}>Couldn't load the Lost reasons from Epicor. Try again.</Alert>
          ) : (
            <Form.Select value={lostReason} onChange={(e) => setLostReason(e.target.value)} disabled={openCloseBusy}>
              <option value="">- pick a reason -</option>
              {lostReasons.map((x) => (
                <option key={x.reasonCode} value={x.reasonCode}>{x.description || x.reasonCode}</option>
              ))}
            </Form.Select>
          )}
          {reportMsg?.type === "danger" && (
            <Alert variant="danger" style={{ fontSize: 13, marginTop: 10, marginBottom: 0 }}>{reportMsg.text}</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" disabled={openCloseBusy} onClick={() => setCloseOpen(false)}>Cancel</Button>
          <Button variant="danger" size="sm" disabled={!lostReason || openCloseBusy} onClick={() => openCloseQuote(true)}>
            {openCloseBusy ? <Spinner animation="border" size="sm" /> : "Close as lost"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Documents modal ────────────────────────────────────────────────
          A modal, not a side drawer: the drawer began at the top of the window,
          so its header — and the close button in it — sat behind the fixed
          navbar and the user had no visible way out.

          Three tabs rather than one scroll. The quote PDF goes OUT to the
          customer, the POs come IN from them, and the attachments are internal
          and never sent — stacking them implied one list with one owner.

          Every icon is one indigo family. The first pass used red for PDFs,
          indigo for POs and green for attachments, which read as three
          severities rather than three categories. ── */}
      {loadedQuote && (
      <Modal show={docsOpen} onHide={() => setDocsOpen(false)} size="lg" centered scrollable
             dialogClassName="qe-docs-modal">
        <Modal.Header closeButton>
          <div>
            <Modal.Title style={{ fontSize: 16.5, fontWeight: 700, letterSpacing: "-.01em" }}>Documents</Modal.Title>
            <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>
              Quote #{loadedQuote.quoteNum} · {loadedQuote.customerName || loadedQuote.customerCustID}
            </div>
          </div>
        </Modal.Header>

        <div className="qe-tabs">
          <button type="button" className={`qe-tab${docsTab === "pdf" ? " on" : ""}`} onClick={() => setDocsTab("pdf")}>
            Quote PDF <span className="c">{REPORT_STYLES.length}</span>
          </button>
          <button type="button" className={`qe-tab${docsTab === "po" ? " on" : ""}`}
                  onClick={() => { if (poDocs === null) loadPODocs(); setDocsTab("po"); }}>
            Customer POs <span className="c">{poDocs ? poDocs.length : "—"}</span>
          </button>
          <button type="button" className={`qe-tab${docsTab === "att" ? " on" : ""}`} onClick={() => setDocsTab("att")}>
            Attachments
          </button>
        </div>

        <Modal.Body style={{ padding: 0 }}>
          {docsTab === "pdf" && (
            hasAction("00033", true) ? (
              <>
                <div className="qe-intro">
                  Three layouts of the same quote — pick what the customer should see.
                </div>
                {REPORT_STYLES.map((st) => (
                  <button type="button" key={st.value} className="qe-doc"
                          /* every row disables while any one is in flight, so a
                             second click cannot start a parallel download — but
                             only the row actually running says so. */
                          disabled={!!downloading}
                          onClick={() => handleDownload(st.value)}>
                    <span className="ic">
                      {downloading === st.value ? <Spinner animation="border" size="sm" /> : <FaFileAlt />}
                    </span>
                    <span className="m">
                      <span className="n">{st.label}</span>
                      <span className="d">
                        {downloading === st.value ? "Preparing…" : "PDF · generated on download"}
                      </span>
                    </span>
                    <span className="go"><FaDownload /></span>
                  </button>
                ))}
              </>
            ) : (
              <div className="qe-empty">Your role is not entitled to download quote documents.</div>
            )
          )}

          {docsTab === "po" && (
            poLoading ? (
              <div className="qe-empty"><Spinner animation="border" size="sm" /> Loading PO documents…</div>
            ) : (poDocs && poDocs.length > 0) ? (
              <>
                <div className="qe-intro">
                  Purchase orders the customer has sent for this quote. Conversion needs one
                  matching <strong>{fmtMoney(amountDueForDocs)}</strong> within $1.00.
                </div>
                {poDocs.map((att) => (
                  <button type="button" key={att.id} className="qe-doc" title={poLabel(att)}
                          onClick={() => downloadOnePO(att).catch((e) => setReportMsg({ type: "danger", text: `PO download failed: ${e.message}` }))}>
                    <span className="ic"><FaFileAlt /></span>
                    <span className="m"><span className="n">{poLabel(att)}</span></span>
                    <span className="go"><FaDownload /></span>
                  </button>
                ))}
              </>
            ) : (
              <div className="qe-empty">
                <span className="big"><FaFileAlt /></span>
                No purchase orders uploaded yet.<br />
                Conversion needs a PO matching {fmtMoney(amountDueForDocs)}.
              </div>
            )
          )}

          {docsTab === "att" && (
            <>
              <div className="qe-intro">
                Notes, photos and files your team keeps against this quote. These are
                internal — they are never sent to the customer.
              </div>
              <div style={{ padding: "10px 18px 18px" }}>
                {/* The existing reference-docs component — it owns the list, the
                    uploader and entitlement 00060. Restyled via its style prop:
                    its default is white-on-translucent-white for the purple
                    ribbon, which rendered invisible on this white surface. */}
                <AttachmentsButton
                  entityType="Quote"
                  entityId={loadedQuote.quoteNum}
                  style={{ background: "#4F46E5", color: "#fff", fontWeight: 700 }}
                />
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="qe-docs-foot">
          <span className="note">
            {docsTab === "pdf" && "Generated on download — always reflects the current quote."}
            {docsTab === "po" && (poDocs && poDocs.length > 0 ? `${poDocs.length} document${poDocs.length === 1 ? "" : "s"}` : "Upload POs from the quote list or the conversion screen.")}
            {docsTab === "att" && "Internal only — never sent to the customer."}
          </span>
          {docsTab === "po" && poDocs && poDocs.length > 1 && (
            <Button className="qw-btn qw-btn-sm qe-docs-pri" disabled={poDownloading} onClick={handleDownloadAllPOs}>
              <FaDownload style={{ marginRight: 6 }} /> {poDownloading ? "Preparing…" : `Download all (${poDocs.length})`}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
      )}

      {loadError && <Alert variant="danger" style={{ marginBottom: 12 }}>{loadError}</Alert>}

      {!loadedQuote && !loading && (
        <div style={{ margin: "0 auto" }}>
          <Alert variant="light">Enter a quote number above to begin.</Alert>
        </div>
      )}

      {loadedQuote && (
        <div style={{ margin: "0 auto" }}>
          {/* Edit toolbar — the view-mode actions moved into the ribbon
              header; this row now only appears while editing (change summary
              + Cancel/Save, unchanged behavior). */}
          {isEditing && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
              <div style={{ fontSize: 13, color: "#64748B" }}>
                {hasAnyChanges
                  ? <><strong>Unsaved changes:</strong> {summarizeChanges(headerEdits, lineEdits, validAddLines, removedLines, recalculateTariff)}</>
                  : "Make changes below, then Save."}
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", rowGap: 10 }}>
                <Button variant="outline-secondary" onClick={cancelEdit} disabled={submitting}>Cancel</Button>
                <Button variant="primary" onClick={handleSubmit} disabled={!hasAnyChanges || submitting || badNewLineQty}
                  title={badNewLineQty ? NEW_LINE_QTY_RULE : undefined} style={{ fontWeight: 600 }}>
                  {submitting ? <><Spinner animation="border" size="sm" /> Saving…</> : "Save changes"}
                </Button>
              </div>
            </div>
          )}

          {isConvertedToOrder && !isMspQuote && (
            <Alert variant="info" style={{ marginBottom: 12 }}>
              <strong>This quote has been converted to a sales order.</strong> It can&apos;t be edited while a valid
              order exists. To make changes, <strong>void the sales order</strong> first — the quote then reopens for editing.
            </Alert>
          )}
          {isMspQuote && (
            <Alert variant="info" style={{ marginBottom: 12 }}>
              <strong>Multi-shipment quote.</strong> Each shipment is ordered by its own customer PO and converts
              on its own — a &quot;closed&quot; or &quot;ordered&quot; flag here is never final until every shipment has
              converted. Manage POs and conversions on the <strong>Shipments &amp; POs</strong> board.
            </Alert>
          )}

          {submitResult && (
            <Alert
              variant={submitResult.success ? "success" : "danger"}
              dismissible
              onClose={() => setSubmitResult(null)}
              style={{ marginBottom: 12 }}
            >
              {submitResult.success
                ? "Quote updated successfully."
                : <><strong>Update failed.</strong> {submitResult.error}</>}
            </Alert>
          )}

          {reportMsg && (
            <Alert variant={reportMsg.type} dismissible onClose={() => setReportMsg(null)} style={{ marginBottom: 12 }}>
              {reportMsg.text}
            </Alert>
          )}

          <SendQuoteEmailModal
            show={sendOpen}
            onHide={() => setSendOpen(false)}
            quoteNum={loadedQuote.quoteNum}
            customerName={loadedQuote.custID || loadedQuote.customerName}
            onResult={(msg) => { setReportMsg(msg); setSendOpen(false); }}
          />

          <ConvertToOrderModal
            show={convertOpen}
            onHide={() => setConvertOpen(false)}
            quote={loadedQuote}
            onConverted={(msg) => {
              setConvertOpen(false);
              setReportMsg(msg);
              // Reload so the quote reflects its new "ordered" (read-only) state.
              loadQuote(loadedQuote.quoteNum);
              loadLogs(loadedQuote.quoteNum);
            }}
          />

          {/* View mode = read-only review; Edit mode = full summary (read-only
              context) + inline-editable cards below. */}
          {!isEditing ? (
            <QuoteHeaderSummary quote={loadedQuote} logs={logs} />
          ) : (
            <>
              <QuoteHeaderSummary quote={loadedQuote} logs={logs} hideLines />
              <div className="qw-eyebrow" style={{ margin: "4px 0 8px" }}>Edit</div>
              <EditTab
                key={editSessionKey}
              quote={loadedQuote}
              logs={logs}
              headerEdits={headerEdits}
              setHeaderEdits={setHeaderEdits}
              lineEdits={lineEdits}
              setLineEdits={setLineEdits}
              addLines={addLines}
              setAddLines={setAddLines}
              removedLines={removedLines}
              setRemovedLines={setRemovedLines}
              recalculateTariff={recalculateTariff}
              setRecalculateTariff={setRecalculateTariff}
              tariffEnabled={tariffEnabled}
              />
            </>
          )}

          {/* Secondary: audit log + failed jobs (view mode only, keeps focus while editing) */}
          {!isEditing && (
            <div style={{ marginTop: 24 }}>
              <Tabs defaultActiveKey="logs" className="mb-3">
                <Tab eventKey="logs" title={<>Audit Log {logs.length > 0 && <Badge bg="secondary">{logs.length}</Badge>}</>}>
                  <AuditLogTab
                    logs={logs}
                    loading={logsLoading}
                    error={logsError}
                    onInspectJob={openJobModal}
                    onRefresh={() => loadLogs(loadedQuote.quoteNum)}
                  />
                </Tab>
                <Tab eventKey="jobs" title={<>Failed Jobs {failedJobCount(logs) > 0 && <Badge bg="danger">{failedJobCount(logs)}</Badge>}</>}>
                  <FailedJobsTab logs={logs} onInspectJob={openJobModal} />
                </Tab>
              </Tabs>
            </div>
          )}
        </div>
      )}

      {jobModal && (
        <JobInspectorModal
          modal={jobModal}
          onClose={() => setJobModal(null)}
          onRetry={hasAction("00038", true) ? retryJob : undefined}
        />
      )}
      </div>
    </Container>
  );
}

// =============================================================================
// QuoteHeaderSummary — read-only at-a-glance card above the tabs
// =============================================================================
// Pair each Epicor quote line with its saved pricing snapshot (active
// SalesQuoteLog) so the edit screen can show the SAME breakdown the
// create-quote review shows. Logs carry the full breakdown but no part # /
// line #, so each log is matched to a line by selling price (consumed once).
function buildLineReviewModels(lines, logs, termsCode) {
  const num = (v) => Number(v) || 0;
  // A multi-shipment quote writes ONE SalesQuoteLog row for the whole plan
  // (its sellingPrice is the system total), so no log matches an individual
  // kit line by price — the log-derived plan code falls back to N30 and every
  // MSP line would wear the wrong pill. The quote's terms code is the
  // authority here.
  const mspQuote = isMspTermsCode(termsCode);
  const activeLogs = (logs || []).filter(
    (l) => String(l.status || "").toLowerCase() !== "voided");
  const usedLog = new Set();

  return lines.map((l, idx) => {
    const lineSelling = num(l.docExtPriceDtl) || num(l.docExpUnitPrice) * num(l.orderQty);

    // closest unused active log by sellingPrice
    let bestI = -1, bestDiff = Infinity;
    activeLogs.forEach((lg, i) => {
      if (usedLog.has(i)) return;
      const diff = Math.abs(num(lg.sellingPrice) - lineSelling);
      if (diff < bestDiff) { bestDiff = diff; bestI = i; }
    });
    let lg = null;
    if (bestI >= 0 && bestDiff <= Math.max(1, lineSelling * 0.02)) {
      lg = activeLogs[bestI];
      usedLog.add(bestI);
    }

    return {
      key: l.quoteLine ?? idx,
      partNum: l.partNum || l.xPartNum || "—",
      desc: l.lineDesc || l.partNumPartDescription || "",
      qty: num(l.orderQty) || 1,
      // docExpUnitPrice is the per-unit figure Epicor prices from; sellingPrice
      // below is EXTENDED (unit x qty). Carrying both lets the table show
      // "unit x qty = ext" instead of one number whose basis the reader has to
      // infer from a 10px multiplier.
      unitPrice: num(l.docExpUnitPrice) || (num(l.orderQty) ? lineSelling / num(l.orderQty) : lineSelling),
      planCode: mspQuote ? "MSP" : (lg?.planCode || "N30"),
      numberOfMonths: lg?.numberOfMonths || null,
      sellingPrice: lg ? num(lg.sellingPrice) : lineSelling,
      systemDiscount: lg ? num(lg.systemDiscount) : num(l.docDiscount),
      repDiscountAmount: lg ? num(lg.repDiscountAmount) : 0,
      repDiscountPct: lg ? num(lg.repDiscountPct) : 0,
      tariffAmount: lg ? num(lg.tariffAmount) : 0,
      setupFee: lg ? num(lg.setupFee) : 0,
      interestAmount: lg ? num(lg.interestAmount) : 0,
      invoiceFee: lg ? num(lg.invoiceFee) : 0,
      taxAmount: lg ? num(lg.taxAmount) : 0,
      totalPrice: lg ? num(lg.totalPrice) : (lineSelling - num(l.docDiscount)),
      monthlyPayment: lg ? num(lg.monthlyPayment) : 0,
      // The line's place in the kit structure, as the API names it ("parent" /
      // "component" / "ordinary", D-040 addendum 30). A component's price is
      // already inside its parent's, so the footer leaves it out of the sums.
      // An API that sends no mark leaves every line "ordinary" - the old sums.
      lineRole: String(l.lineRole || "ordinary").toLowerCase(),
      hasLog: !!lg,
    };
  });
}

// Mirror of the backend TermsCodeCodec.DecodePlanCategory: the terms code's
// first char B/P/C → SPLIT/POT/CTO; anything else (incl. "N30" / empty) → N30
// (cash). Only N30 quotes allow line edits / additions — POT / BK / CTO are
// locked payment-plan quotes whose schedule would break if qty/discount/lines
// changed.
// Multi-shipment terms codes are the M{bucket}K family (M1K…M9K, MAK…MZK).
// Recognition is PATTERN-matched, never first-character — the backend's
// TermsCodeCodec deliberately decodes them as CASH (that's how MSP inherits
// N30 invoicing), so the display label must come from this pattern instead.
export function isMspTermsCode(termsCode) {
  return /^M[0-9A-Z]K$/.test(String(termsCode || "").trim().toUpperCase());
}

function decodeTermsPlan(termsCode) {
  const c = String(termsCode || "").trim().toUpperCase();
  if (!c) return "N30";
  if (isMspTermsCode(c)) return "MSP";
  if (c[0] === "B") return "BK";
  if (c[0] === "P") return "POT";
  if (c[0] === "C") return "CTO";
  return "N30";
}

function planPillClass(code) {
  const c = String(code || "").toUpperCase();
  if (c === "POT") return "qw-pill-violet";
  if (c === "BK")  return "qw-pill-indigo";
  if (c === "MSP") return "qw-pill-violet";
  if (c.startsWith("C")) return "qw-pill-amber";   // CTO / C2K…
  return "qw-pill-emerald";                          // N30 / cash
}

// Read-only per-line pricing card — mirrors the create-quote review's
// LinePreview (selling → discounts/fees → total), driven by the saved snapshot.
// Plan-specific extras (tariff / fees / interest / monthly) do not deserve their
// own columns — they appear on POT/CTO/BK lines only. They ride under the part
// description as chips so the table stays six columns wide on every plan.
function lineExtras(m) {
  const nz = (v) => Number.isFinite(Number(v)) && Number(v) !== 0;
  const out = [];
  if (nz(m.tariffAmount))   out.push(`Tariff ${fmtMoney(m.tariffAmount)}`);
  if (nz(m.setupFee))       out.push(`Setup ${fmtMoney(m.setupFee)}`);
  if (nz(m.interestAmount)) out.push(`Interest ${fmtMoney(m.interestAmount)}`);
  if (nz(m.invoiceFee))     out.push(`Invoice fees ${fmtMoney(m.invoiceFee)}`);
  if (m.numberOfMonths && nz(m.monthlyPayment))
    out.push(`${m.numberOfMonths}× ${fmtMoney(m.monthlyPayment)}/mo`);
  return out;
}

// A line's discount is the system discount plus any rep discount — one column,
// because a reader comparing lines cares about "how much came off", not which
// bucket it came from. The split still shows in the quote-level roll-up.
const lineDiscount = (m) => (Number(m.systemDiscount) || 0) + (Number(m.repDiscountAmount) || 0);

// Extended price, computed from what is on screen rather than read from the
// stored snapshot. lg.totalPrice is a frozen figure that never included tax and
// does not move when anything above it changes, so a card built on it could
// show three numbers that visibly do not add up.
const lineExt = (m) => (Number(m.sellingPrice) || 0) - lineDiscount(m);

function LineDetailsTable({ models }) {
  const extrasOf = (m) => lineExtras(m);
  return (
    <>
      {/* ── Desktop: a real table. Unit / Qty / Ext as separate columns so the
             arithmetic is checkable by eye. No per-line Tax column: the backend
             stamps the whole quote's Avalara figure into every line's snapshot
             (CreateSalesQuoteHandler.cs:1280 and six sibling sites), so a
             per-line tax row shows the same number on every line and invites a
             reader to add them up. Tax is quote-level and lives in the footer. */}
      <div className="qw-lt-table">
        <div className="qw-lt-scroll">
          <table className="qw-lt">
            <thead>
              <tr>
                <th style={{ width: 44 }}>#</th>
                <th>Part</th>
                <th className="n">Unit price</th>
                <th className="n">Qty</th>
                <th className="n">Discount</th>
                <th className="n">Ext price</th>
              </tr>
            </thead>
            <tbody>
              {models.map((m) => {
                const disc = lineDiscount(m);
                const extras = extrasOf(m);
                return (
                  <tr key={m.key}>
                    <td className="ln">{m.key}</td>
                    <td>
                      <span className="qw-mono qw-lt-part">{m.partNum}</span>
                      <span className={`qw-pill ${planPillClass(m.planCode)}`} style={{ marginLeft: 7 }}>{m.planCode}</span>
                      {m.desc && <div className="qw-lt-desc">{m.desc}</div>}
                      {extras.length > 0 && (
                        <div className="qw-lt-extras">{extras.join(" · ")}</div>
                      )}
                    </td>
                    <td className="n">{fmtMoney(m.unitPrice)}</td>
                    <td className="n qty">{m.qty}</td>
                    <td className="n">{disc ? <span className="disc">({fmtMoney(disc)})</span> : <span className="dash">—</span>}</td>
                    <td className="n ext">{fmtMoney(lineExt(m))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Phone: the same data, transposed. Six columns cannot fit 372px and a
             sideways-scrolling table is the wrong answer for the page's main
             content, so each line becomes a card — and unit x qty survives as an
             equation row, which is the whole reason the columns exist. */}
      <div className="qw-lt-cards">
        {models.map((m) => {
          const disc = lineDiscount(m);
          const extras = extrasOf(m);
          return (
            <div className="qw-lc" key={m.key}>
              <div className="qw-lc-hd">
                <span className="qw-mono qw-lc-num">{m.key}</span>
                <span className="qw-mono qw-lc-part">{m.partNum}</span>
                <span className={`qw-pill ${planPillClass(m.planCode)}`} style={{ flexShrink: 0 }}>{m.planCode}</span>
              </div>
              <div className="qw-lc-bd">
                {m.desc && <div className="qw-lt-desc" style={{ marginBottom: 9 }}>{m.desc}</div>}
                <div className="qw-eqn">
                  <span className="l"><strong>{fmtMoney(m.unitPrice)}</strong> × {m.qty}</span>
                  <span className="r sub">{fmtMoney(m.sellingPrice)}</span>
                </div>
                {disc > 0 && (
                  <div className="qw-eqn d">
                    <span className="l">less discount</span>
                    <span className="r">({fmtMoney(disc)})</span>
                  </div>
                )}
                {extras.length > 0 && <div className="qw-lt-extras" style={{ paddingTop: 4 }}>{extras.join(" · ")}</div>}
                <div className="qw-eqn tot">
                  <span className="l">Ext price</span>
                  <span className="r">{fmtMoney(lineExt(m))}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

// The order number(s) made from a converted quote. Epicor closes the quote on
// conversion and keeps no order number on it, so they come from our own links:
// the multi-shipment plan's shipments and the review's convert link. A review
// row is trusted only when its customer matches the quote's - quote numbers
// were re-used on the pilot, and an old row would name another customer's
// order (UI-QTE-029).
function useQuoteOrderNums(quote) {
  const [orderNums, setOrderNums] = useState([]);
  const quoteNum = quote?.quoteNum;
  const ordered = !!quote?.ordered;
  const custID = quote?.customerCustID;
  useEffect(() => {
    if (!ordered || !quoteNum) { setOrderNums([]); return undefined; }
    let cancelled = false;
    const get = (path) => fetch(`${API_URL}${path}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null)).catch(() => null);
    Promise.all([
      get(`/api/Msp/quote/${quoteNum}/shipments`),
      get(`/api/QuoteReview/${quoteNum}`),
    ]).then(([msp, review]) => {
      if (cancelled) return;
      const nums = new Set();
      (msp?.shipments || []).forEach((s) => { if (s.orderNum) nums.add(Number(s.orderNum)); });
      if (review && (!review.custID || !custID || review.custID === custID)) {
        (review.orderNums || []).forEach((n) => n && nums.add(Number(n)));
        if (review.orderNum) nums.add(Number(review.orderNum));
      }
      setOrderNums([...nums].sort((a, b) => a - b));
    });
    return () => { cancelled = true; };
  }, [quoteNum, ordered, custID]);
  return orderNums;
}

// The ship-to's name for view mode ("8 · Dock A (Chicago, IL)") — the quote holds
// only the number. Null until loaded, or when the address is not in the list.
function useShipToLabel(custNum, shipToNum) {
  const [label, setLabel] = useState(null);
  useEffect(() => {
    setLabel(null);
    if (!custNum || !shipToNum) return undefined;
    let cancelled = false;
    fetch(`${API_URL}/api/ShipTo?custNum=${encodeURIComponent(custNum)}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        const list = data.shipTos || data.ShipTos || data.shipToes || data.ShipToes || [];
        const s = list.find((x) => String(x?.shipToNum ?? "").trim() === shipToNum);
        if (!s) return;
        const place = [s.city, s.state].filter(Boolean).join(", ");
        setLabel(`${s.name || ""}${place ? ` (${place})` : ""}`.trim() || null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [custNum, shipToNum]);
  return label;
}

function QuoteHeaderSummary({ quote, logs, hideLines = false }) {
  // A converted quote reads "Ordered", not Epicor's "Closed" (SAL-GAP-85, Kapil 2026-10-23).
  const status = quote.ordered ? "Ordered"
               : quote.quoteClosed ? "Closed"
               : quote.expired ? "Expired"
               : quote.quoted ? "Quoted"
               : "Open";
  const statusCls = status === "Ordered" ? "qw-pill-emerald"
                  : status === "Closed" ? "qw-pill-slate"
                  : status === "Expired" ? "qw-pill-amber"
                  : status === "Quoted" ? "qw-pill-indigo"
                  : "qw-pill-emerald";
  const orderNums = useQuoteOrderNums(quote);

  // PO, ship-to and comment in view mode (UI-QED-031), so a rep can check what a
  // save wrote without opening Edit. The PO hides only the value conversion writes
  // on its own ("<PLAN>-<quoteNum>", e.g. BK-133860) — that is not a customer PO.
  const poRaw = String(quote.poNum ?? "").trim();
  const poNumber = poRaw && !new RegExp(`^[A-Za-z0-9]+-${quote.quoteNum}$`).test(poRaw) ? poRaw : "";
  const shipToNum = String(quote.shipToNum ?? "").trim();
  const shipToLabel = useShipToLabel(quote.shipToCustNum || quote.custNum, shipToNum);
  const comment = String(quote.quoteComment ?? "").trim();

  const lines = quote.quoteDtls || [];
  const num = (v) => Number(v) || 0;
  const grand = num(quote.quoteAmt);
  const territory = quote.territoryID
    || (quote.salesRepCode && quote.salesRepCode.includes("-") ? quote.salesRepCode.split("-")[0] : "");

  // Per-line review models + aggregate roll-up from the saved pricing snapshot.
  const lineModels = buildLineReviewModels(lines, logs, quote.termsCode);
  // Only the lines that price the quote are added up: kit parents and ordinary
  // lines. On a multi-shipment quote every component line is already inside its
  // parent's price (quote 133998: three parents of $5,333.33 and thirty
  // components - $16,000.00 of lines, not $32,000.00). The table still lists
  // every line; only the footer sums leave the components out.
  const pricedModels = lineModels.filter((m) => m.lineRole !== "component");
  const componentCount = lineModels.length - pricedModels.length;
  const parentCount = lineModels.filter((m) => m.lineRole === "parent").length;
  const anyLog = lineModels.some((m) => m.hasLog);
  const sum = (f) => pricedModels.reduce((s, m) => s + (m[f] || 0), 0);
  const rollup = {
    sellingPrice: sum("sellingPrice"),
    systemDiscount: sum("systemDiscount"),
    repDiscountAmount: sum("repDiscountAmount"),
    tariffAmount: sum("tariffAmount"),
    setupFee: sum("setupFee"),
    interestAmount: sum("interestAmount"),
    invoiceFee: sum("invoiceFee"),
    // NOT sum("taxAmount"). Every line's snapshot carries the WHOLE quote's
    // Avalara tax (CreateSalesQuoteHandler.cs:1280 and six sibling sites), so
    // summing them multiplied the tax by the line count — this quote rendered
    // two rows both labelled "Tax", one of them doubled. The header figure
    // (quote.totalTax, shown once below) is the authoritative one.
    taxAmount: 0,
    totalPrice: sum("totalPrice"),
  };
  // Extended line money from the saved pricing snapshot, computed from the same
  // numbers the table prints. Only used to cross-check Epicor (UI-QED-007).
  const lineExtTotal = pricedModels.reduce(
    (t, m) => t + ((Number(m.sellingPrice) || 0) - lineDiscount(m)), 0);
  // UI-QED-007: lines, Amount due and the PO figure all come from EPICOR's own quote
  // totals — the conversion PO check (CoverageService) uses them, so the page must
  // too. The snapshot's totalPrice already holds the plan fees, which Epicor keeps as
  // line charges, so using it as "lines" counted the fees twice (134078); and an old
  // quote can hold a price the snapshot never reached (133782: Epicor $21,525.00, no
  // discount; snapshot $15,999.53), which the amber note below says out loud.
  const snapshotDisagrees = anyLog && Math.abs(lineExtTotal - grand) >= 0.01;

  // Header-level charges live outside the lines, so neither quoteAmt nor the
  // per-line roll-up includes them. Conversion, however, requires a PO matching
  // lines + misc + tax within $1 — the same sum CoverageService.Calculate uses
  // server-side. Showing only the line total sent reps to the customer for the
  // wrong amount, and the PO then failed the match with no visible reason.
  const miscAmt = num(quote.totalMiscAmt);
  const taxAmt = num(quote.totalTax);
  const amountDue = Math.round((grand + miscAmt + taxAmt) * 100) / 100;
  const hasHeaderCharges = Math.abs(amountDue - grand) >= 0.01;

  // Every charge Epicor holds on this quote, one row each (Issue 25 / OBS-34):
  // the header charges (the shipping estimate, a tariff) and the line charges
  // (the trade-in credit STID, a rep discount SRRD, plan fees). Epicor's own
  // totalMiscAmt is the sum of both kinds, and it used to appear as ONE
  // "Misc charges (header)" row - hidden altogether when the net was negative,
  // which is exactly the trade-in case (133997: a $2,152.50 credit and a $60
  // shipping estimate vanished into "charges -$1,052.53" with no row).
  const chargeRows = [];
  (quote.quoteHedMscs || []).forEach((m, i) => chargeRows.push({
    key: `h${m.seqNum ?? i}`, code: m.miscCode || "", desc: m.description || m.miscCode || "Charge",
    amount: num(m.docMiscAmt) || num(m.miscAmt), where: "quote",
  }));
  lines.forEach((l) => (l.quoteMscs || []).forEach((m, i) => chargeRows.push({
    key: `l${l.quoteLine}-${m.seqNum ?? i}`, code: m.miscCode || "", desc: m.description || m.miscCode || "Charge",
    amount: num(m.docMiscAmt) || num(m.miscAmt), where: `line ${l.quoteLine}`,
  })));
  // Plan fees (setup, interest, invoice fees) are Epicor LINE charges, itemised in the
  // rows above; the snapshot's fee rows are shown only when Epicor itemises none.
  const hasLineCharges = chargeRows.some((r) => r.where !== "quote");
  const chargesListed = Math.round(chargeRows.reduce((t, r) => t + r.amount, 0) * 100) / 100;
  // Anything Epicor counts that is not itemised above still foots the total.
  const chargesUnlisted = Math.round((miscAmt - chargesListed) * 100) / 100;

  // Expiry is the one date that CHANGES WHAT A REP CAN DO: an expired quote is
  // refused by ConvertQuoteToOrderHandler.cs:275 and the Convert button is
  // already disabled for it. Showing the bare date would only explain a block
  // after it happened, so show the days remaining too — a rep who sees "in 6
  // days" can still act, one who sees a greyed button has lost the deal.
  // (followUpDate deliberately NOT shown: it is written by Epicor and read by
  // nothing — no job, no filter, no UI — so displaying it would imply a
  // workflow that does not exist.)
  const expiryInfo = (() => {
    if (!quote.expirationDate) return null;
    // Count CALENDAR days (UI-QED-008). Epicor sends the date as a zone-less
    // midnight ("2026-10-21T00:00:00"); counting hours from now made every label a
    // day short after the morning and showed a quote "expired" on its last day.
    // Take the date part as a local date and compare it with today's local date.
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(quote.expirationDate));
    const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(quote.expirationDate);
    if (Number.isNaN(d.getTime())) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);
    const days = Math.round((d - today) / 86400000);   // round absorbs a DST hour
    return {
      date: d.toLocaleDateString(),
      days,
      label: quote.expired || days < 0 ? "expired" : days === 0 ? "today" : `in ${days}d`,
      tone: quote.expired || days < 0 ? "bad" : days <= 14 ? "warn" : "calm",
    };
  })();

  return (
    <div className="qw-root">
      {/* Two FULL-WIDTH bands, not a two-column row.
          The identity/facts block and the pricing roll-up carry very different
          amounts of content, so side-by-side always left a hole — inside the
          short card with align-items:stretch, beside it with :start. Stacking
          them removes the mismatch outright: each band ends where its own
          content does, and nothing depends on the two happening to match.
          The facts use qw-grid-auto, which packs 2-6 columns by available
          width, so this stays one short strip on desktop and stacks on a
          phone. */}
      <div style={{ marginBottom: 12 }}>
      <div className="qw-card-flat qw-pad">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
          {/* Quote number AND customer together — a bare "#133872" identifies
              nothing to a human. They are the two things that say which record
              this is, so they belong side by side. Customer is therefore NOT
              repeated in the facts strip below. */}
          <div style={{ display: "flex", gap: 28, flexWrap: "wrap", minWidth: 0 }}>
            <div>
              <div className="qw-eyebrow">Quote</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 2 }}>
                <span className="qw-mono" style={{ fontSize: 20, fontWeight: 700, color: "#0F172A", lineHeight: 1.1 }}>#{quote.quoteNum}</span>
                <span className={`qw-pill ${statusCls}`}>
                  {status}
                  {status === "Ordered" && orderNums.length > 0 && (
                    <> · {orderNums.map((n, i) => (
                      <React.Fragment key={n}>
                        {i > 0 && ", "}
                        <a href={`/order/${n}`} style={{ color: "inherit" }}>#{n}</a>
                      </React.Fragment>
                    ))}</>
                  )}
                </span>
              </div>
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="qw-eyebrow">Customer</div>
              <div style={{ marginTop: 2, minWidth: 0 }}>
                <div style={{ fontSize: 16.5, fontWeight: 700, color: "#0F172A", lineHeight: 1.2 }}>
                  {quote.customerName || "—"}
                </div>
                {quote.customerCustID && (
                  <div className="qw-mono" style={{ fontSize: 12.5, color: "#94A3B8", marginTop: 1 }}>
                    {quote.customerCustID}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="qw-eyebrow">{hasHeaderCharges ? "Amount due" : "Total"}</div>
            {/* Lead with the figure that actually has to be paid / PO-matched.
                When header charges exist the line total is shown beneath it, so
                both numbers are visible and the difference is self-explanatory. */}
            <div className="qw-mono" style={{ fontSize: 20, fontWeight: 700, color: "#0052A5", lineHeight: 1.1 }}>
              {fmtMoney(hasHeaderCharges ? amountDue : grand)}
            </div>
            {hasHeaderCharges && (
              <div style={{ fontSize: 11.5, color: "#64748B", marginTop: 2 }}>
                lines {fmtMoney(grand)} + charges {fmtMoney(miscAmt + taxAmt)}
              </div>
            )}
          </div>
        </div>
        {/* The PO rule as a full-width band, not a box in the right-hand column.
            Stacked under Amount due it made that column ~100px taller than the
            identity column beside it, leaving a hole under the customer — the
            third time today a short block next to a tall one produced one. It
            is also a sentence, and a sentence reads better across the row than
            wrapped in a 340px box.

            It deliberately does NOT name quote.poNum. That field looks like a
            customer PO but is "<PLAN>-<quoteNum>" written automatically at
            conversion (BK-133860, N30-133872) and empty before it — printing it
            here would tell a rep a PO was on file when none is. Real customer
            POs are the PODocument attachments in the Documents modal. */}
        {hasHeaderCharges && (
          <div className="qw-po-band">
            <span className="k">The customer's PO must match</span>
            <span className="v qw-mono">{fmtMoney(amountDue)}</span>
            <span className="t">
              within $1.00 — <strong>not</strong> the {fmtMoney(grand)} line total.
              Charges sit outside the lines.
            </span>
          </div>
        )}

        <div className="qw-grid-auto qw-facts" style={{ fontSize: 13, gap: "12px 26px", paddingTop: 12, borderTop: "1px solid #EDF0F5" }}>
          {/* Customer intentionally absent — it moved up beside the quote number.

              ORDER MATTERS HERE. The strip is auto-fit, so it is six columns on
              a large monitor, three at mid-width and TWO on a phone — meaning
              positions 1 and 2 are the only ones visible at every size. They go
              to the two facts that change what a rep does:
                1. Terms   — B210 / N30 / PoT is the commercial shape of the deal
                             and drives invoicing, conversion and PO matching.
                2. Expires — the only actionable deadline; it blocks Convert.
              Then ownership (rep, and the territory derived from it), then size,
              then the least actionable date. */}
          <SummaryItem label="Terms" value={quote.termsCode || "—"} />
          {expiryInfo && (
            <div className="qw-sum-row">
              <span className="qw-sum-label">Expires</span>
              <span className="qw-sum-value">
                {expiryInfo.date}
                <span className={`qw-expiry qw-expiry-${expiryInfo.tone}`}>{expiryInfo.label}</span>
              </span>
            </div>
          )}
          <SummaryItem label="Sales rep" value={quote.salesRepCode} sub={quote.salesRepName} />
          <SummaryItem label="Territory" value={territory || "—"} />
          <SummaryItem label="Lines" value={`${lines.length} line${lines.length === 1 ? "" : "s"}`}
            sub={componentCount > 0
              ? `${parentCount} kit parent${parentCount === 1 ? "" : "s"} + ${componentCount} component${componentCount === 1 ? "" : "s"}`
              : undefined} />
          <SummaryItem label="Entered" value={quote.entryDate ? fmtDateTime(quote.entryDate).split(",")[0] : "—"} />
          <SummaryItem label="PO number" value={poNumber || "—"} />
          <SummaryItem label="Ship to" value={shipToNum || "Default (sold-to)"} sub={shipToLabel || undefined} />
        </div>
        {comment && (
          <div style={{ fontSize: 13, paddingTop: 10, marginTop: 10, borderTop: "1px solid #EDF0F5", display: "flex", gap: 10 }}>
            <span className="qw-sum-label" style={{ flex: "none" }}>Comment</span>
            <span style={{ color: "#0F172A", whiteSpace: "pre-wrap", minWidth: 0 }}>{comment}</span>
          </div>
        )}
      </div>

      </div>

      {/* Per-line review cards — one per line, mirroring the create review.
          Hidden in edit mode (hideLines) because the editor renders its own
          line cards; the summary above still gives the full header context. */}
      {!hideLines && lineModels.length > 0 && (
        <div>
          <div className="qw-eyebrow" style={{ marginBottom: 8 }}>Line details</div>
          <div className="qw-card-flat" style={{ overflow: "hidden" }}>
            <LineDetailsTable models={lineModels} />
            {/* Footer totals, computed from the columns above plus the
                quote-level header charges — so what is on screen adds up. Tax
                is taken from quote.totalTax (the header figure), never from the
                per-line snapshots: those each hold the whole quote's tax, so
                summing them multiplies it by the line count. */}
            <div className="qw-lt-foot">
              {/* NOT className="row" — that is Bootstrap's grid class, and its
                  `.row > *` rule forces width:100% on every child, which stacked
                  each label above its amount instead of opposing them. */}
              {/* The ONE money breakdown on this page, directly beneath the lines
                  it is derived from. It used to be duplicated in a "Pricing
                  summary" card two sections up, which restated Misc / Tax /
                  Amount due verbatim and left most of a full-width card empty. */}
              {snapshotDisagrees ? (
                // The snapshot's split cannot be shown under Epicor's line total — it
                // would not add up — so Epicor's own gross and discount are shown.
                <>
                  <div className="qw-ltf-row"><span>Selling price</span><span>{fmtMoney(num(quote.totalGrossValue) || grand)}</span></div>
                  {num(quote.totalDiscount) > 0 && <div className="qw-ltf-row"><span>Discount</span><span className="neg">({fmtMoney(num(quote.totalDiscount))})</span></div>}
                </>
              ) : (
                <>
                  <div className="qw-ltf-row"><span>Selling price</span><span>{fmtMoney(rollup.sellingPrice)}</span></div>
                  {rollup.systemDiscount > 0 && <div className="qw-ltf-row"><span>System discount</span><span className="neg">({fmtMoney(rollup.systemDiscount)})</span></div>}
                  {rollup.repDiscountAmount > 0 && <div className="qw-ltf-row"><span>Rep discount</span><span className="neg">({fmtMoney(rollup.repDiscountAmount)})</span></div>}
                  {rollup.tariffAmount > 0 && <div className="qw-ltf-row"><span>Tariff</span><span>{fmtMoney(rollup.tariffAmount)}</span></div>}
                </>
              )}
              {!hasLineCharges && rollup.setupFee > 0 && <div className="qw-ltf-row"><span>Setup fee</span><span>{fmtMoney(rollup.setupFee)}</span></div>}
              {!hasLineCharges && rollup.interestAmount > 0 && <div className="qw-ltf-row"><span>Interest</span><span>{fmtMoney(rollup.interestAmount)}</span></div>}
              {!hasLineCharges && rollup.invoiceFee > 0 && <div className="qw-ltf-row"><span>Invoice fees</span><span>{fmtMoney(rollup.invoiceFee)}</span></div>}
              {componentCount > 0 && (
                <div className="qw-ltf-row note">
                  <span>{componentCount} kit component line{componentCount === 1 ? "" : "s"} priced inside {parentCount} kit parent{parentCount === 1 ? "" : "s"} above, not added again</span>
                </div>
              )}
              <div className="qw-ltf-row sub"><span>Line total</span><span>{fmtMoney(grand)}</span></div>
              {snapshotDisagrees && (
                <div className="qw-ltf-row note" style={{ background: "#FFFBEB", color: "#92400E", borderRadius: 6, padding: "6px 8px" }}>
                  <span>
                    Epicor holds these lines at {fmtMoney(grand)}; the saved pricing says {fmtMoney(lineExtTotal)}
                    {" "}({grand > lineExtTotal ? "+" : "−"}{fmtMoney(Math.abs(grand - lineExtTotal))}). The PO and the order follow Epicor.
                  </span>
                </div>
              )}
              {chargeRows.map((r) => (
                <div className="qw-ltf-row" key={r.key}>
                  <span>{r.desc}{r.code && r.code !== r.desc ? ` (${r.code})` : ""} <span className="qw-ltf-where">· {r.where}</span></span>
                  {r.amount < 0
                    ? <span className="neg">({fmtMoney(-r.amount)})</span>
                    : <span>{fmtMoney(r.amount)}</span>}
                </div>
              ))}
              {Math.abs(chargesUnlisted) >= 0.01 && (
                <div className="qw-ltf-row"><span>Other charges (not itemised)</span>
                  {chargesUnlisted < 0 ? <span className="neg">({fmtMoney(-chargesUnlisted)})</span> : <span>{fmtMoney(chargesUnlisted)}</span>}
                </div>
              )}
              {taxAmt > 0 && <div className="qw-ltf-row"><span>Tax · quote level</span><span>{fmtMoney(taxAmt)}</span></div>}
              <div className="qw-ltf-row grand"><span>{hasHeaderCharges ? "Amount due" : "Total"}</span><span>{fmtMoney(amountDue)}</span></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// SendQuoteEmailModal — resend the quote report by email. Standard recipients
// (sales rep, distributor, customer contact) are resolved server-side via the
// /email endpoint; any "additional recipients" go through the /send endpoint.
// =============================================================================
function SendQuoteEmailModal({ show, onHide, quoteNum, customerName, onResult }) {
  const [variant, setVariant]       = useState(DEFAULT_REPORT_STYLE);
  const [toStandard, setToStandard] = useState(true);
  const [extra, setExtra]           = useState("");
  const [sending, setSending]       = useState(false);
  const [err, setErr]               = useState(null);

  // Reset each time the modal is (re)opened.
  useEffect(() => {
    if (show) {
      setVariant(DEFAULT_REPORT_STYLE);
      setToStandard(true);
      setExtra("");
      setErr(null);
      setSending(false);
    }
  }, [show]);

  const extras = extra.split(/[,\n;]/).map((s) => s.trim()).filter(Boolean);
  const canSend = (toStandard || extras.length > 0) && !sending;

  const handleSend = async () => {
    setErr(null);
    if (!toStandard && extras.length === 0) {
      setErr("Pick the standard recipients or add at least one email.");
      return;
    }
    const bad = extras.find((e) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));
    if (bad) { setErr(`"${bad}" is not a valid email address.`); return; }

    setSending(true);
    // Run the two sends independently — a failure in one must not abort the
    // other (e.g. unresolved standard recipients shouldn't block a valid
    // additional recipient).
    const sent = [];
    const failed = [];
    try {
      if (toStandard) {
        try {
          await resendQuoteEmail(quoteNum, variant);
          sent.push("sales rep, distributor & customer contact");
        } catch (e) {
          failed.push(`standard recipients (${e.message})`);
        }
      }
      if (extras.length) {
        const label = `${extras.length} additional recipient${extras.length === 1 ? "" : "s"}`;
        try {
          await sendQuoteReportTo(quoteNum, variant, extras);
          sent.push(label);
        } catch (e) {
          failed.push(`${label} (${e.message})`);
        }
      }

      if (sent.length && !failed.length) {
        onResult({ type: "success", text: `Quote ${quoteNum} emailed to ${sent.join(" and ")}.` });
      } else if (sent.length && failed.length) {
        // Partial success — report what went out and what didn't.
        onResult({ type: "warning", text: `Quote ${quoteNum} emailed to ${sent.join(" and ")}, but failed for ${failed.join(" and ")}.` });
      } else {
        // Everything failed — keep the modal open so the user can fix and retry.
        setErr(failed.join("; "));
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 18 }}>Send quote {quoteNum}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {customerName && (
          <div style={{ fontSize: 12.5, color: "#64748B", marginBottom: 12 }}>Customer {customerName}</div>
        )}
        <Form.Group className="mb-3">
          <Form.Label className="small fw-semibold">Report style</Form.Label>
          <Form.Select value={variant} onChange={(e) => setVariant(e.target.value)}>
            {REPORT_STYLES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Form.Select>
        </Form.Group>
        <Form.Check
          type="checkbox"
          id="send-standard-recipients"
          className="mb-3"
          checked={toStandard}
          onChange={(e) => setToStandard(e.target.checked)}
          label={<span className="qw-send-lbl">Send to the sales rep, distributor &amp; customer contact <span className="muted">(resolved automatically)</span></span>}
        />
        <Form.Group className="mb-2">
          <Form.Label className="small fw-semibold">Additional recipients (optional)</Form.Label>
          <Form.Control as="textarea" rows={2} value={extra} onChange={(e) => setExtra(e.target.value)}
            placeholder="email@example.com, another@example.com" />
          <Form.Text muted>Comma- or newline-separated. The first becomes the To, the rest are CC&apos;d.</Form.Text>
        </Form.Group>
        {err && <Alert variant="danger" style={{ marginBottom: 0 }}>{err}</Alert>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={sending}>Cancel</Button>
        <Button variant="primary" onClick={handleSend} disabled={!canSend}>
          {sending ? (
            /* An envelope that actually flies, rather than a spinning ring.
               A spinner says "something is happening"; this says "your email is
               on its way", which is the one thing the rep wants to know. */
            <span className="qw-sending is-sending">
              <span className="env"><FaEnvelope /></span>
              <span className="lbl">Sending…</span>
            </span>
          ) : (
            <span className="qw-sending">
              <span className="env"><FaEnvelope /></span>
              <span className="lbl">Send</span>
            </span>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ConvertToOrderModal lives in ./ConvertToOrderModal.js — the guided
// upload → coverage → contacts → review → done flow (imported above).

// =============================================================================
// EditTab — header editor + line list + misc tariff toggle + submit
// =============================================================================
function EditTab({
  quote, logs, headerEdits, setHeaderEdits,
  lineEdits, setLineEdits, addLines, setAddLines,
  removedLines, setRemovedLines,
  recalculateTariff, setRecalculateTariff, tariffEnabled = true,
}) {
  const setHeader = (k, v) => setHeaderEdits((h) => {
    const next = { ...h };
    if (v === "" || v === null || v === undefined) delete next[k];
    else next[k] = v;
    return next;
  });

  // ── Ship-To locations for the header picker ──────────────────────────────
  // Ship-To Number used to be free text, so a rep could type a number that
  // doesn't exist on the customer (or blank it) and only find out when the
  // order failed. Offer the customer's real locations instead: `inactive=false`
  // drops retired addresses, and the blank-ShipToNum row Epicor returns for
  // every customer is dropped here — it mirrors the main office and is the
  // implicit default, not a location you pick. "0" is a genuine ship-to for
  // some customers, so the test is emptiness, never falsiness.
  const [shipTos, setShipTos] = useState([]);
  const [shipTosFailed, setShipTosFailed] = useState(false);
  const custNum = quote?.custNum;
  useEffect(() => {
    if (!custNum) { setShipTos([]); setShipTosFailed(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${API_URL}/api/ShipTo?custNum=${encodeURIComponent(custNum)}&inactive=false`,
          { headers: authHeaders() },
        );
        const data = await r.json().catch(() => null);
        if (cancelled) return;
        // The API answers `shipTos`; `shipToes` was the old monolith's key and
        // reading only it left this picker empty (UI-QED-031).
        const list = (data?.shipTos || data?.ShipTos || data?.shipToes || data?.ShipToes || [])
          .filter(Boolean)
          .filter((s) => String(s.shipToNum ?? "").trim() !== "");
        setShipTos(list);
        setShipTosFailed(!r.ok);
      } catch {
        if (!cancelled) { setShipTos([]); setShipTosFailed(true); }
      }
    })();
    return () => { cancelled = true; };
  }, [custNum]);

  // The quote's current ship-to may predate this list (retired address, or a
  // value typed before the picker existed). Keep it selectable so opening the
  // editor and saving an unrelated field can't silently retarget the shipment.
  const currentShipTo = String(quote?.shipToNum ?? "").trim();
  const shipToOptions = currentShipTo && !shipTos.some((s) => String(s.shipToNum) === currentShipTo)
    ? [{ shipToNum: currentShipTo, name: "current value", __orphan: true }, ...shipTos]
    : shipTos;

  const setLine = (qLine, k, v) => setLineEdits((edits) => {
    const next = { ...edits };
    const cur  = { ...(next[qLine] || {}) };
    if (v === "" || v === null || v === undefined || Number.isNaN(v)) delete cur[k];
    else cur[k] = v;
    if (Object.keys(cur).length === 0) delete next[qLine];
    else next[qLine] = cur;
    return next;
  });

  const toggleRemove = (qLine) => setRemovedLines((set) => {
    const next = new Set(set);
    if (next.has(qLine)) next.delete(qLine); else next.add(qLine);
    return next;
  });

  // ── New lines ──────────────────────────────────────────────────────────────
  const addNewLine = () => setAddLines((rows) => [
    ...rows, { partNum: "", partDesc: "", quantity: 1, repDiscountPercent: 0 },
  ]);
  const setNewLine = (idx, patch) => setAddLines((rows) =>
    rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const removeNewLine = (idx) => setAddLines((rows) => rows.filter((_, i) => i !== idx));

  const lines = quote.quoteDtls || [];

  // Only N30 (cash / Net-30) quotes allow line edits + additions. POT / BK / CTO
  // are locked payment-plan quotes — qty / discount / line changes would break
  // the agreed payment schedule, so those controls are disabled.
  const planCategory = decodeTermsPlan(quote.termsCode);
  const isN30 = planCategory === "N30";

  // Rep discount is per SYSTEM (an S9-prefixed line), never on reorder parts. On
  // CREATE it's folded into the Epicor line DiscountPercent (system + rep combined),
  // so we read each line's rep % back from the pricing logs (SalesQuoteLog.repDiscountPct),
  // matched to its line exactly the way the read-only review does.
  const repPctByLine = {};
  buildLineReviewModels(lines, logs, quote.termsCode).forEach((m) => { repPctByLine[m.key] = m.repDiscountPct || 0; });
  const isSystemLine = (l) => (l.partNum || "").toUpperCase().startsWith("S9");

  return (
    <div className="qw-root">
      {/* ─ Header edits ─────────────────────────────────────────────────── */}
      <div className="qw-card-flat qw-pad" style={{ marginBottom: 12 }}>
        <div className="qw-eyebrow" style={{ marginBottom: 10 }}>Header</div>
        <Row className="g-2">
          <Col xs={12} md={6}>
            <Form.Label className="small mb-1">PO Number</Form.Label>
            <Form.Control size="sm" defaultValue={quote.poNum || ""}
              onChange={(e) => setHeader("poNum", e.target.value)} placeholder="(unchanged)" />
          </Col>
          <Col xs={12} md={6}>
            <Form.Label className="small mb-1">Ship-To Number</Form.Label>
            {/* Falls back to free text when the lookup failed or the customer
                has no numbered locations — never block an edit on the picker. */}
            {shipTosFailed || shipToOptions.length === 0 ? (
              <Form.Control size="sm" defaultValue={quote.shipToNum || ""}
                onChange={(e) => setHeader("shipToNum", e.target.value)} placeholder="(unchanged)" />
            ) : (
              <Form.Select size="sm" defaultValue={quote.shipToNum || ""}
                onChange={(e) => setHeader("shipToNum", e.target.value)}>
                <option value="">(unchanged)</option>
                {shipToOptions.map((s) => {
                  const place = [s.city, s.state].filter(Boolean).join(", ");
                  return (
                    <option key={`qe-st-${s.shipToNum}`} value={s.shipToNum}>
                      {s.shipToNum} · {s.name || "—"}{place ? ` (${place})` : ""}{s.primaryShipTo ? " · primary" : ""}
                    </option>
                  );
                })}
              </Form.Select>
            )}
          </Col>
          <Col xs={12} md={6}>
            <Form.Label className="small mb-1">Comments</Form.Label>
            <Form.Control size="sm" defaultValue={quote.quoteComment || ""}
              onChange={(e) => setHeader("comments", e.target.value)} placeholder="(unchanged)" />
          </Col>
          <Col xs={6} md={3}>
            <Form.Label className="small mb-1">Header Discount %</Form.Label>
            {/* BK lines are locked to the balanced-split price, so a header discount
                would change every instalment - the server refuses it too. */}
            <Form.Control size="sm" type="number" step="0.01" min={0} max={100}
              defaultValue={quote.discountPercent || ""}
              disabled={planCategory === "BK"}
              title={planCategory === "BK" ? "BK quote - its lines are locked, so a header discount does not apply." : undefined}
              onChange={(e) => setHeader("headerDiscountPercent", e.target.value === "" ? null : Number(e.target.value))}
              placeholder="(unchanged)" />
            {/* UI-QED-033: the server adds this on top of each line's system discount. */}
            <Form.Text muted style={{ fontSize: 11 }}>
              {planCategory === "BK"
                ? "Not available on BK quotes - the lines are locked."
                : "Added on top of each line's system discount."}
            </Form.Text>
          </Col>
          <Col xs={6} md={3}>
            <Form.Label className="small mb-1">Sales Rep Code</Form.Label>
            <Form.Control size="sm" defaultValue={quote.salesRepCode || ""}
              onChange={(e) => setHeader("salesRepCode", e.target.value)} placeholder="(unchanged)" />
          </Col>
        </Row>
      </div>

      {/* ─ Lines — one editable card per line (mirrors the review cards) ──── */}
      <div className="qw-eyebrow" style={{ marginBottom: 8 }}>Line details</div>
      {!isN30 && (
        <Alert variant="warning" style={{ fontSize: 13 }}>
          This is a <strong>{planCategory}</strong> payment-plan quote — its lines are locked.
          Quantity, discount and add/remove line changes are only available on
          <strong> Net-30 (N30)</strong> quotes, where they won't break a payment schedule.
        </Alert>
      )}
      {lines.length === 0 && (
        <Alert variant="light" style={{ fontSize: 13 }}>This quote has no lines.</Alert>
      )}
      {lines.map((l) => {
        const removed = removedLines.has(l.quoteLine);
        return (
          <div key={l.quoteLine} style={{
            border: removed ? "1px dashed #dc3545" : "1px solid #E2E8F0",
            borderRadius: 12, overflow: "hidden", marginBottom: 12,
            background: removed ? "rgba(220,53,69,0.04)" : "#fff",
          }}>
            <div style={{ padding: "8px 14px", background: "#F8FAFC", borderBottom: "1px solid #EEF2F7", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, color: "#94A3B8" }}>Line {l.quoteLine}</div>
                <span className="qw-mono" style={{ fontWeight: 700, color: "#334155", fontSize: 13.5 }}>{l.partNum}</span>
                {l.lineDesc && <span style={{ fontSize: 12, color: "#94A3B8", marginLeft: 8 }}>{l.lineDesc}</span>}
              </div>
              <Button
                variant={removed ? "outline-secondary" : "outline-danger"}
                size="sm"
                onClick={() => toggleRemove(l.quoteLine)}
                style={{ flexShrink: 0 }}
                disabled={!isN30}
              >
                {removed ? "Undo Remove" : <><FaTrash /> Remove</>}
              </Button>
            </div>
            {!removed && (
              <div style={{ padding: "10px 14px" }}>
                <Row className="g-2">
                  <Col xs={4} md={3}>
                    <Form.Label className="small mb-1">Qty</Form.Label>
                    <Form.Control size="sm" type="number" min={1} defaultValue={l.orderQty} disabled={!isN30}
                      onChange={(e) => setLine(l.quoteLine, "quantity", e.target.value === "" ? null : Number(e.target.value))} />
                  </Col>
                  <Col xs={8} md={isSystemLine(l) && isN30 ? 5 : 9}>
                    <Form.Label className="small mb-1">Part Number</Form.Label>
                    <Form.Control size="sm" defaultValue={l.partNum} disabled={!isN30}
                      onChange={(e) => setLine(l.quoteLine, "partNum", e.target.value)} />
                  </Col>
                  {/* Rep discount is entitlement-gated (00079 — CustomerService / IT);
                      other roles see no slider and the stored discount stays as-is. */}
                  {isSystemLine(l) && isN30 && hasAction("00079", true) && (
                    <Col xs={12} md={4}>
                      <Form.Label className="small mb-1">
                        Rep Discount % <Badge bg="info">{lineEdits[l.quoteLine]?.repDiscountPercent ?? repPctByLine[l.quoteLine] ?? 0}%</Badge>
                      </Form.Label>
                      <Form.Range min={0} max={20} step={1} defaultValue={repPctByLine[l.quoteLine] || 0} disabled={!isN30}
                        onChange={(e) => setLine(l.quoteLine, "repDiscountPercent", Number(e.target.value))} />
                    </Col>
                  )}
                </Row>
                <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 6 }}>
                  Current: qty {l.orderQty}, list {fmtMoney(l.listPrice)}, ext {fmtMoney(l.extPriceDtl)}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* ─ Add new lines (N30 only — these ride along as Net-30 reorder lines) ─ */}
      {isN30 && addLines.map((row, idx) => (
        <div key={`new-${idx}`} style={{
          border: "1px solid #BFD9F2", borderRadius: 12, overflow: "hidden",
          marginBottom: 12, background: "rgba(0,82,165,0.03)",
        }}>
          <div style={{ padding: "8px 14px", background: "#EFF6FF", borderBottom: "1px solid #DBEAFE", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, color: "#0052A5", fontWeight: 700 }}>
              New line {idx + 1}
            </div>
            <Button variant="outline-danger" size="sm" onClick={() => removeNewLine(idx)} style={{ flexShrink: 0 }}>
              <FaTrash /> Remove
            </Button>
          </div>
          <div style={{ padding: "10px 14px" }}>
            <Row className="g-2">
              <Col xs={12} md={(row.partNum || "").toUpperCase().startsWith("S9") ? 5 : 9}>
                <Form.Label className="small mb-1">System (part) *</Form.Label>
                <SearchableSelect
                  config={AUTOCOMPLETE_PART}
                  value={row.partNum}
                  onChange={(val) => setNewLine(idx, { partNum: (val || "").toUpperCase() })}
                  onSelectItem={(item) => setNewLine(idx, {
                    partNum: (item.Part_PartNum || item.partNum || "").toUpperCase(),
                    partDesc: item.Part_PartDescription || "",
                  })}
                  placeholder="Type part # or system name…"
                  inputWidth="100%"
                  secondaryText={row.partDesc}
                />
              </Col>
              <Col xs={4} md={3}>
                <Form.Label className="small mb-1">Qty</Form.Label>
                <Form.Control size="sm" type="number" min={1} value={row.quantity}
                  isInvalid={newLineQtyError(row.quantity) !== null}
                  onChange={(e) => setNewLine(idx, { quantity: e.target.value === "" ? "" : Number(e.target.value) })} />
                {newLineQtyError(row.quantity) && (
                  <Form.Control.Feedback type="invalid">{newLineQtyError(row.quantity)}</Form.Control.Feedback>
                )}
              </Col>
              {(row.partNum || "").toUpperCase().startsWith("S9") && hasAction("00079", true) && (
                <Col xs={12} md={4}>
                  <Form.Label className="small mb-1">
                    Rep Discount % <Badge bg="info">{row.repDiscountPercent || 0}%</Badge>
                  </Form.Label>
                  <Form.Range min={0} max={20} step={1} value={row.repDiscountPercent || 0}
                    onChange={(e) => setNewLine(idx, { repDiscountPercent: Number(e.target.value) })} />
                </Col>
              )}
            </Row>
          </div>
        </div>
      ))}
      <Button variant="outline-primary" size="sm" onClick={addNewLine} disabled={!isN30}
        style={{ marginBottom: 12, fontWeight: 600 }}
        title={isN30 ? "Add a Net-30 line" : `${planCategory} quotes can't add lines`}>
        <FaPlus style={{ marginRight: 6 }} /> Add line
      </Button>

      {/* ─ Tariff (hidden while the surcharge is switched off) ──────────── */}
      {tariffEnabled && <div className="qw-card-flat qw-pad" style={{ marginTop: 4 }}>
        <div className="qw-eyebrow" style={{ marginBottom: 8 }}>Tariff</div>
        <ButtonGroup size="sm">
          <Button variant={recalculateTariff === null ? "primary" : "outline-primary"}
            onClick={() => setRecalculateTariff(null)}>Leave as-is</Button>
          <Button variant={recalculateTariff === true ? "primary" : "outline-primary"}
            onClick={() => setRecalculateTariff(true)}>Recalculate</Button>
          <Button variant={recalculateTariff === false ? "primary" : "outline-primary"}
            onClick={() => setRecalculateTariff(false)}>Remove</Button>
        </ButtonGroup>
      </div>}
    </div>
  );
}

// Gap 16 (2026-10-31): the server's quantity rule (QuoteQuantityRule), in the same words.
const NEW_LINE_QTY_RULE = "Quantity must be at least 1.";
const NEW_LINE_WHOLE_RULE = "Quantity must be a whole number.";
function newLineQtyError(quantity) {
  const q = Number(quantity);
  if (quantity === "" || quantity == null || !Number.isFinite(q) || q < 1) return NEW_LINE_QTY_RULE;
  return Number.isInteger(q) ? null : NEW_LINE_WHOLE_RULE;
}

// A refused request whose body is ASP.NET's validation shape ({ title, errors: { field: [..] } })
// carries its reasons in `errors`; show the first one rather than a bare "HTTP 400".
function validationText(data) {
  const first = data && data.errors && Object.values(data.errors).flat().find(Boolean);
  return first ? String(first) : null;
}

function summarizeChanges(headerEdits, lineEdits, addLines, removedLines, recalculateTariff) {
  const parts = [];
  if (Object.keys(headerEdits).length) parts.push(`${Object.keys(headerEdits).length} header field(s)`);
  if (Object.keys(lineEdits).length)   parts.push(`${Object.keys(lineEdits).length} line(s) edited`);
  if (addLines.length)                 parts.push(`${addLines.length} line(s) to add`);
  if (removedLines.size)               parts.push(`${removedLines.size} line(s) to remove`);
  if (recalculateTariff !== null)      parts.push(recalculateTariff ? "tariff recalculate" : "tariff remove");
  return parts.join(", ");
}

// =============================================================================
// AuditLogTab — list of SalesQuoteLog entries, with "Inspect Job" buttons
// =============================================================================
function AuditLogTab({ logs, loading, error, onInspectJob, onRefresh }) {
  if (loading) return <div style={{ padding: 20 }}><Spinner animation="border" size="sm" /> Loading audit log…</div>;
  if (error)   return <Alert variant="warning">Could not load logs: {error}</Alert>;
  if (logs.length === 0) {
    return (
      <Alert variant="light">
        No audit log entries yet for this quote.
        <Button variant="link" size="sm" onClick={onRefresh} style={{ padding: 0, marginLeft: 8 }}>refresh</Button>
      </Alert>
    );
  }
  return (
    <Card>
      <Card.Body>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          <h6 style={{ color: "#0052A5", fontWeight: 700, margin: 0 }}>Audit Log ({logs.length})</h6>
          <Button variant="outline-secondary" size="sm" onClick={onRefresh}><FaSync /> Refresh</Button>
        </div>
        {/* `qe-audit` + the data-label on each cell drive a phone-only restack
            (see .qe-audit in DynamicQw.css). These 8 columns are 476px wide, so
            on a 440px screen the row ran past the edge and was only readable by
            swiping sideways inside the .table-responsive wrapper. data-* render
            nothing, so the desktop table is untouched. */}
        <Table hover responsive size="sm" style={{ fontSize: 13 }} className="qe-audit">
          <thead>
            <tr style={{ background: "#f5f7fa" }}>
              <th>When</th>
              <th>By</th>
              <th>Status</th>
              <th>Plan</th>
              <th className="text-end">Selling</th>
              <th className="text-end">Total</th>
              <th className="text-end">Commission</th>
              <th>Job</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <tr key={log.logId}>
                <td data-label="When">{fmtDateTime(log.createdAt)}</td>
                <td data-label="By">{log.createdBy || "—"}</td>
                <td data-label="Status"><StatusBadge value={log.status} /></td>
                <td data-label="Plan">{log.planCode}{log.numberOfMonths ? ` (${log.numberOfMonths}mo)` : ""}</td>
                <td className="text-end" data-label="Selling">{fmtMoney(log.sellingPrice)}</td>
                <td className="text-end" data-label="Total">{fmtMoney(log.totalPrice)}</td>
                <td className="text-end" data-label="Commission">
                  {fmtMoney(log.commissionAmount)}
                  <div style={{ fontSize: 11, color: "#888" }}>{fmtPct(log.actualCommPct)}</div>
                </td>
                <td data-label="Job">
                  {log.erpJobId ? (
                    <Button variant="link" size="sm" style={{ padding: 0 }} onClick={() => onInspectJob(log.erpJobId)}>
                      inspect
                    </Button>
                  ) : <span style={{ color: "#888" }}>—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
}

function StatusBadge({ value }) {
  const v = (value || "").toLowerCase();
  const bg = v === "created" || v === "approved" || v === "retrysucceeded" ? "success"
           : v === "failed"  || v === "retryfailed"   ? "danger"
           : v === "rejected" || v === "expired"      ? "secondary"
           : "info";
  return <Badge bg={bg}>{value || "—"}</Badge>;
}

// =============================================================================
// FailedJobsTab — distilled view of just the failed/retryable entries
// =============================================================================
function failedJobCount(logs) {
  return logs.filter((l) => isRetryableLog(l)).length;
}
function isRetryableLog(log) {
  const s = (log.status || "").toLowerCase();
  return !!log.erpJobId && (s === "failed" || s === "retryfailed");
}

function FailedJobsTab({ logs, onInspectJob }) {
  const failed = logs.filter(isRetryableLog);
  if (failed.length === 0) {
    return <Alert variant="success"><FaCheckCircle /> No failed jobs for this quote.</Alert>;
  }
  return (
    <Card>
      <Card.Body>
        <h6 style={{ color: "#dc3545", fontWeight: 700 }}>
          <FaExclamationTriangle style={{ marginRight: 6 }} />
          {failed.length} failed job{failed.length === 1 ? "" : "s"}
        </h6>
        <p style={{ fontSize: 13, color: "#666" }}>
          Inspect a job to view its step trace, then retry if it's safe to (status=Failed, compensation=RolledBack/NotRequired, retries remaining).
        </p>
        <Table hover size="sm">
          <thead>
            <tr style={{ background: "#f5f7fa", fontSize: 13 }}>
              <th>When</th>
              <th>Status</th>
              <th>Plan</th>
              <th>Job ID</th>
              <th></th>
            </tr>
          </thead>
          <tbody style={{ fontSize: 13 }}>
            {failed.map((log) => (
              <tr key={log.logId}>
                <td>{fmtDateTime(log.createdAt)}</td>
                <td><StatusBadge value={log.status} /></td>
                <td>{log.planCode}</td>
                <td><code style={{ fontSize: 11 }}>{String(log.erpJobId).slice(0, 8)}…</code></td>
                <td>
                  <Button variant="primary" size="sm" onClick={() => onInspectJob(log.erpJobId)}>
                    Inspect & Retry
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
}

// =============================================================================
// JobInspectorModal — shows job/{id} steps + a Retry button when eligible
// =============================================================================
function JobInspectorModal({ modal, onClose, onRetry }) {
  const job = modal?.job;
  const canRetry =
    onRetry &&
    job &&
    job.status === "Failed" &&
    (job.compensationStatus === "RolledBack" || job.compensationStatus === "NotRequired") &&
    (job.retryCount ?? 0) < (job.maxRetries ?? 0);

  return (
    <Modal show onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>
          Job <code style={{ fontSize: 13 }}>{modal.jobId}</code>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {modal.loading && <div><Spinner animation="border" size="sm" /> Loading job…</div>}
        {modal.error && <Alert variant="danger">{modal.error}</Alert>}
        {job && (
          <>
            <Row className="g-2 mb-2" style={{ fontSize: 13 }}>
              <Col xs={6} md={3}><strong>Type:</strong> {job.jobType || "—"}</Col>
              <Col xs={6} md={3}><strong>Status:</strong> <StatusBadge value={job.status} /></Col>
              <Col xs={6} md={3}><strong>Compensation:</strong> {job.compensationStatus || "—"}</Col>
              <Col xs={6} md={3}><strong>Retries:</strong> {job.retryCount ?? 0}/{job.maxRetries ?? 0}</Col>
            </Row>
            {job.failedStep && (
              <Alert variant="warning" style={{ fontSize: 12 }}>
                <strong>Failed at:</strong> {job.failedStep}<br />
                {job.failedReason}
              </Alert>
            )}
            <h6 style={{ marginTop: 12 }}>Steps</h6>
            <Table size="sm" hover style={{ fontSize: 12 }}>
              <thead>
                <tr><th>#</th><th>Step</th><th>Status</th><th>Started</th><th>Completed</th></tr>
              </thead>
              <tbody>
                {(job.steps || []).map((s, i) => (
                  <tr key={i}>
                    <td>{s.stepOrder}</td>
                    <td>{s.stepName}</td>
                    <td><StatusBadge value={s.status} /></td>
                    <td>{fmtDateTime(s.startedAt)}</td>
                    <td>{fmtDateTime(s.completedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {modal.retryResult && (
              <Alert
                variant={modal.retryResult.ok ? "success" : "danger"}
                style={{ marginTop: 8, marginBottom: 0 }}
              >
                {modal.retryResult.message}
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Close</Button>
        {canRetry && (
          <Button
            variant="warning"
            disabled={modal.retrying}
            onClick={() => onRetry(modal.jobId)}
          >
            {modal.retrying ? <><Spinner animation="border" size="sm" /> Retrying…</> : <><FaSync /> Retry job</>}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default QuoteEdit;
